import React, { useState, useEffect } from "react";
import { 
   
  Box, 
  Input, 
  Button, 
  FormControl, 
  FormErrorMessage, 
  FormLabel, 
  Text, 
  Stack, 
  HStack, 
  Divider, 
  InputGroup, 
  InputRightAddon,
  Spinner,
  Center
} from "@chakra-ui/react";
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import PrimaryButton from '../../components/themeComponents/PrimaryButton';
import { Outlet, useOutletContext } from 'react-router-dom';
import PlatformWidget from '../../components/themeComponents/PlatformWidget';
import { integrateShopify } from '../../Services/IntegrationService';
import ShopifyIcon from "../../images/Shopify.png";
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../Services/axiosInstance";


export default function Shopify() {
  const { integration } = useOutletContext();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [shopName, setShopName] = useState('');
  const [shopifyDetails, setShopifyDetails] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Fetch integration status
  const checkIntegrationStatus = async () => {
    setIsLoading(true);
    try {
      // Replace with your actual endpoint for checking Shopify integration status
      const response = await axiosInstance.get('/integrations/shopify');
      
      const data = response.data;
      
      if (data.isIntegrated && data.platform === 'shopify' && data.shopifyDetails?.hasShopify) {
        setIsConnected(true);
        setShopName(data.shopifyDetails.shopName || '');
        setShopifyDetails(data.shopifyDetails);
      } else {
        setIsConnected(false);
      }
    } catch (error) {
      console.error('Failed to fetch Shopify integration status:', error);
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkIntegrationStatus();
  }, []);

  const onSubmit = (values) => {
    integrateShopify(values).then((res) => {
      window.open(res.data, "_self");
    }).catch((err) => {
      console.log(err);
    });
  };

  const handleRemove = async () => {
    if (!window.confirm(t('Are you sure you want to disconnect Shopify?'))) return;
    
    setRemoving(true);
    try {
      await axiosInstance.post('/integrations/shopify/remove');
      setIsConnected(false);
      setShopName('');
      setShopifyDetails(null);
      // Optionally refresh the status
      await checkIntegrationStatus();
    } catch (error) {
      console.error('Failed to remove Shopify integration:', error);
      alert(t('Failed to disconnect Shopify. Please try again.'));
    } finally {
      setRemoving(false);
    }
  };

  const validationSchema = Yup.object({
    shopName: Yup.string().required(t('shopify.store.name.error.required')),
  });

  const WidgetContent = () => {
    if (isConnected) {
      return (
        <Box width="100%" textAlign="center" py={6}>
          <Text fontSize="1.1rem" color="#7746A6" fontWeight="semibold" mb={4}>
            {t('Connected to')}: <strong>{shopName}</strong>
          </Text>
          
          <Button
            colorScheme="red"
            variant="solid"
            onClick={handleRemove}
            isLoading={removing}
            loadingText={t('Removing...')}
            size="md"
          >
            {t('Remove Connection')}
          </Button>
          
          <Text fontSize="0.85rem" color="gray.500" mt={6}>
            {t('You can reconnect anytime')}
          </Text>
        </Box>
      );
    }

    return (
      <Box>
        <Text fontSize={'0.8vw'} width={'60%'} my={'10px'} >
          {t('integrate.shopify.flow')}
        </Text>
        
        <Formik
          initialValues={{ shopName: '' }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          <HStack>
            <Form>
              <Field name="shopName">
                {({ field, form }) => (
                  <FormControl 
                    isInvalid={form.errors.shopName && form.touched.shopName}
                    mb={4}
                  >
                    <Box 
                      width={'100%'} 
                      gap={'0.5vw'} 
                      fontSize={'0.8vw'} 
                      display={'flex'} 
                      alignItems={'baseline'} 
                      color={'#AD88F1'} 
                    >
                      <InputGroup>
                        <Input
                          border={'1px solid #AD88F1'}
                          height={'4vh'}
                          color={'#AD88F1'}
                          {...field}
                          id="shopName"
                          placeholder={t('shopify.store.name.placeholder')}
                        />
                        <InputRightAddon height={'4vh'}>
                          .myshopify.com
                        </InputRightAddon>
                      </InputGroup>
                    </Box>
                    <FormErrorMessage>{form.errors.shopName}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              
              <PrimaryButton 
                width={'8vw'} 
                marginTop={'10px'} 
                type={'submit'} 
                borderRadius={'8px'} 
                height={'1.5vh'} 
                padding={'1vw 1vw'} 
                fontSize={'0.8vw'} 
                label={t('connect')} 
              />
            </Form>
          </HStack>
        </Formik>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box 
        bg={'#fff'} 
        borderRadius={'1vw'} 
        display={'flex'} 
        flexDirection={'column'} 
        p={'3rem'} 
        justifyContent={'center'} 
        alignItems={'center'} 
        minH="400px"
      >
        <Center>
          <Spinner 
            thickness="4px" 
            speed="0.65s" 
            emptyColor="gray.200" 
            color="#AD88F1" 
            size="xl" 
          />
        </Center>
        <Text mt={4} color="#7746A6">Checking Shopify integration status...</Text>
      </Box>
    );
  }

  return (
    <>
      <Box 
        bg={'#fff'} 
        borderRadius={'1vw'} 
        display={'flex'} 
        flexDirection={'column'} 
        p={'1rem'} 
        justifyContent={'center'} 
        alignItems={'center'} 
      >
        <Box 
          alignItems={'baseline'} 
          display={'flex'} 
          flexDirection={'column'} 
          gap={'1vh'} 
          width={'100%'} 
        >
          <PlatformWidget 
            platform={{
              title: t('connect.shopify.store', { 
                integrationName: integration.entityName 
              }),
            }}
            icon={ShopifyIcon}
            WidgetContent={WidgetContent}
          />

          {!isConnected && (
            <>
              <Text 
                fontSize={'xl'} 
                pl={'3%'} 
                color={'#AD88F1'}
              >
                {t('steps.integrate.shopify', { 
                  integrationName: integration.entityName 
                })}
              </Text>
              
              <HStack paddingLeft={'6%'} gap={'1vw'} width={'100%'}>
                <Divider 
                  border={'1px solid #dadada'} 
                  orientation='vertical' 
                  height={'20vh'} 
                />
                <Stack my={'2vh'}>
                  <Stack p={'2rem'}>
                    <Text color={'#7746A6'}>{t('step1.enter.store.name')}</Text>
                    <Text color={'#7746A6'}>{t('step2.click.connect')}</Text>
                    <Text color={'#7746A6'}>{t('step3.install.app')}</Text>
                    <Text color={'#7746A6'}>{t('step4.click.enable')}</Text>
                    <Text as={'i'} mt={'2vh'}>{t('note.data.delay')}</Text>
                  </Stack>
                </Stack>
              </HStack>
            </>
          )}
        </Box>
      </Box>
      <Outlet />
    </>
  );
}